<template>
  <div class="jurisdiction-page">
    <!-- Page Header -->
    <div class="page-header">
      <div>
        <h1 class="page-title">{{ $t('admin.compliance.jurisdiction.title') }}</h1>
        <p class="page-subtitle">{{ $t('admin.compliance.jurisdiction.subtitle') }}</p>
      </div>
      <button class="btn btn-primary" @click="openCreateModal">{{ $t('admin.compliance.jurisdiction.btn_add') }}</button>
    </div>

    <!-- Filter Bar -->
    <div class="filter-bar">
      <div class="filter-group">
        <label class="filter-label">{{ $t('admin.compliance.jurisdiction.filter_country') }}</label>
        <input
          v-model="filters.country"
          type="text"
          class="filter-input"
          :placeholder="$t('admin.compliance.jurisdiction.filter_country_placeholder')"
          maxlength="2"
          @input="resetAndFetch"
        />
      </div>
      <div class="filter-group">
        <label class="filter-label">{{ $t('admin.compliance.jurisdiction.filter_rule_type') }}</label>
        <select v-model="filters.rule_type" @change="resetAndFetch">
          <option value="">{{ $t('admin.compliance.jurisdiction.filter_all') }}</option>
          <option v-for="rt in RULE_TYPES" :key="rt.value" :value="rt.value">{{ rt.label }}</option>
        </select>
      </div>
      <div class="filter-group">
        <label class="filter-label">{{ $t('admin.compliance.jurisdiction.filter_active') }}</label>
        <select v-model="filters.active" @change="resetAndFetch">
          <option value="">{{ $t('admin.compliance.jurisdiction.filter_all') }}</option>
          <option value="true">{{ $t('admin.compliance.jurisdiction.filter_active_yes') }}</option>
          <option value="false">{{ $t('admin.compliance.jurisdiction.filter_active_no') }}</option>
        </select>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-state">
      <span class="spinner"></span>
      {{ $t('admin.compliance.jurisdiction.loading') }}
    </div>

    <!-- Table -->
    <div v-else class="table-container">
      <table>
        <thead>
          <tr>
            <th class="col-country">{{ $t('admin.compliance.jurisdiction.col_country') }}</th>
            <th class="col-region">{{ $t('admin.compliance.jurisdiction.col_region') }}</th>
            <th class="col-type">{{ $t('admin.compliance.jurisdiction.col_rule_type') }}</th>
            <th class="col-summary">{{ $t('admin.compliance.jurisdiction.col_rule_summary') }}</th>
            <th class="col-active">{{ $t('admin.compliance.jurisdiction.col_active') }}</th>
            <th class="col-priority">{{ $t('admin.compliance.jurisdiction.col_priority') }}</th>
            <th class="col-actions">{{ $t('admin.compliance.jurisdiction.col_actions') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="rules.length === 0">
            <td colspan="7" class="empty-row">{{ $t('admin.compliance.jurisdiction.empty') }}</td>
          </tr>
          <tr v-for="rule in rules" :key="rule.id" class="rule-row">
            <td>
              <span class="country-tag">{{ rule.country_code }}</span>
            </td>
            <td class="text-secondary">{{ rule.region_code ?? '—' }}</td>
            <td>
              <span class="type-tag" :class="typeTagClass(rule.rule_type)">{{ formatRuleType(rule.rule_type) }}</span>
            </td>
            <td class="summary-cell">{{ summariseRuleValue(rule.rule_type, rule.rule_value) }}</td>
            <td>
              <span class="badge" :class="rule.is_active ? 'badge-active' : 'badge-inactive'">
                {{ rule.is_active ? $t('admin.compliance.jurisdiction.filter_active_yes') : $t('admin.compliance.jurisdiction.filter_active_no') }}
              </span>
            </td>
            <td class="mono-value">{{ rule.priority }}</td>
            <td>
              <div class="action-buttons">
                <button class="btn-icon" title="Edit Rule" @click="openEditModal(rule)">
                  <AppIcon name="action-view" :size="14" />
                </button>
                <button class="btn-icon btn-icon-danger" title="Delete Rule" @click="confirmDelete(rule)">
                  <AppIcon name="action-ban" :size="14" />
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Pagination -->
      <div v-if="totalPages > 1" class="pagination">
        <button class="pagination-btn" :disabled="currentPage <= 1" @click="goToPage(currentPage - 1)">&larr;</button>
        <button
          v-for="page in visiblePages"
          :key="page"
          class="pagination-btn"
          :class="{ active: page === currentPage }"
          @click="goToPage(page)"
        >{{ page }}</button>
        <span class="pagination-info">{{ $t('admin.compliance.jurisdiction.showing', { start: rangeStart, end: rangeEnd, total: totalRules }) }}</span>
        <button class="pagination-btn" :disabled="currentPage >= totalPages" @click="goToPage(currentPage + 1)">&rarr;</button>
      </div>
    </div>

    <!-- Create / Edit Modal -->
    <div v-if="formModal.visible" class="modal-overlay" @click.self="closeModal">
      <div class="modal-content modal-wide">
        <div class="modal-header">
          <h3 class="modal-title">{{ formModal.editId ? $t('admin.compliance.jurisdiction.modal_edit_title') : $t('admin.compliance.jurisdiction.modal_add_title') }}</h3>
          <button class="modal-close" @click="closeModal">&times;</button>
        </div>
        <div class="modal-body">
          <!-- Country / Region -->
          <div class="form-row">
            <div class="form-group">
              <label class="form-label">{{ $t('admin.compliance.jurisdiction.field_country_code') }} <span class="required">*</span></label>
              <input
                v-model="form.country_code"
                type="text"
                class="form-input"
                :placeholder="$t('admin.compliance.jurisdiction.field_country_code_placeholder')"
                maxlength="2"
                style="text-transform: uppercase"
              />
              <p v-if="formErrors.country_code" class="field-error">{{ formErrors.country_code }}</p>
            </div>
            <div class="form-group">
              <label class="form-label">{{ $t('admin.compliance.jurisdiction.field_region_code') }}</label>
              <input
                v-model="form.region_code"
                type="text"
                class="form-input"
                :placeholder="$t('admin.compliance.jurisdiction.field_region_code_placeholder')"
                maxlength="10"
                style="text-transform: uppercase"
              />
            </div>
          </div>

          <!-- Rule Type -->
          <div class="form-group">
            <label class="form-label">{{ $t('admin.compliance.jurisdiction.field_rule_type') }} <span class="required">*</span></label>
            <select v-model="form.rule_type" class="form-select" @change="resetRuleValue">
              <option value="">{{ $t('admin.compliance.jurisdiction.field_rule_type_placeholder') }}</option>
              <option v-for="rt in RULE_TYPES" :key="rt.value" :value="rt.value">{{ rt.label }}</option>
            </select>
            <p v-if="formErrors.rule_type" class="field-error">{{ formErrors.rule_type }}</p>
          </div>

          <!-- Dynamic rule_value form -->
          <div v-if="form.rule_type" class="rule-value-form">
            <div class="form-group-title">{{ $t('admin.compliance.jurisdiction.rule_config_title') }}</div>

            <!-- blocked_country -->
            <template v-if="form.rule_type === 'blocked_country'">
              <div class="form-group">
                <label class="form-label">{{ $t('admin.compliance.jurisdiction.field_block_message') }}</label>
                <input
                  v-model="form.rule_value_blocked_country.message"
                  type="text"
                  class="form-input"
                  :placeholder="$t('admin.compliance.jurisdiction.field_block_message_placeholder')"
                />
              </div>
            </template>

            <!-- blocked_games -->
            <template v-else-if="form.rule_type === 'blocked_games'">
              <div class="form-group">
                <label class="form-label">{{ $t('admin.compliance.jurisdiction.field_blocked_game_slugs') }}</label>
                <div v-if="gameSlugsLoading" class="loading-inline">{{ $t('admin.compliance.jurisdiction.loading_games') }}</div>
                <div v-else class="slug-checkboxes">
                  <label
                    v-for="game in gameSlugs"
                    :key="game.slug"
                    class="slug-checkbox-label"
                  >
                    <input
                      type="checkbox"
                      :value="game.slug"
                      :checked="form.rule_value_blocked_games.includes(game.slug)"
                      @change="toggleGameSlug(game.slug)"
                    />
                    <span>{{ game.name }} <code class="slug-code">{{ game.slug }}</code></span>
                  </label>
                </div>
                <p class="form-hint">{{ $t('admin.compliance.jurisdiction.field_blocked_games_hint') }}</p>
              </div>
            </template>

            <!-- deposit_limit -->
            <template v-else-if="form.rule_type === 'deposit_limit'">
              <div class="form-row">
                <div class="form-group">
                  <label class="form-label">{{ $t('admin.compliance.jurisdiction.field_daily_limit') }}</label>
                  <input
                    v-model="form.rule_value_deposit_limit.daily_limit"
                    type="number"
                    class="form-input"
                    placeholder="e.g. 500"
                    min="0"
                    step="1"
                  />
                  <p class="form-hint">{{ $t('admin.compliance.jurisdiction.field_daily_limit_hint') }}</p>
                </div>
                <div class="form-group">
                  <label class="form-label">{{ $t('admin.compliance.jurisdiction.field_monthly_limit') }}</label>
                  <input
                    v-model="form.rule_value_deposit_limit.monthly_limit"
                    type="number"
                    class="form-input"
                    placeholder="e.g. 2000"
                    min="0"
                    step="1"
                  />
                  <p class="form-hint">{{ $t('admin.compliance.jurisdiction.field_monthly_limit_hint') }}</p>
                </div>
              </div>
            </template>

            <!-- bonus_restriction -->
            <template v-else-if="form.rule_type === 'bonus_restriction'">
              <div class="form-group">
                <label class="form-label">
                  <input type="checkbox" v-model="form.rule_value_bonus_restriction.allowed" />
                  &nbsp;{{ $t('admin.compliance.jurisdiction.field_bonuses_allowed') }}
                </label>
              </div>
              <div v-if="form.rule_value_bonus_restriction.allowed" class="form-group">
                <label class="form-label">{{ $t('admin.compliance.jurisdiction.field_max_bonus_pct') }}</label>
                <input
                  v-model="form.rule_value_bonus_restriction.max_bonus_percent"
                  type="number"
                  class="form-input"
                  placeholder="e.g. 100"
                  min="0"
                  max="1000"
                />
              </div>
            </template>

            <!-- required_kyc_tier -->
            <template v-else-if="form.rule_type === 'required_kyc_tier'">
              <div class="form-group">
                <label class="form-label">{{ $t('admin.compliance.jurisdiction.field_kyc_tier') }} <span class="required">*</span></label>
                <select v-model="form.rule_value_kyc_tier.tier" class="form-select">
                  <option value="">{{ $t('admin.compliance.jurisdiction.field_kyc_tier_placeholder') }}</option>
                  <option value="basic">{{ $t('admin.compliance.jurisdiction.kyc_tier_basic') }}</option>
                  <option value="enhanced">{{ $t('admin.compliance.jurisdiction.kyc_tier_enhanced') }}</option>
                  <option value="full">{{ $t('admin.compliance.jurisdiction.kyc_tier_full') }}</option>
                </select>
              </div>
            </template>

            <!-- allowed_payment_methods -->
            <template v-else-if="form.rule_type === 'allowed_payment_methods'">
              <div class="form-group">
                <label class="form-label">{{ $t('admin.compliance.jurisdiction.field_payment_methods') }}</label>
                <div class="slug-checkboxes">
                  <label v-for="method in PAYMENT_METHODS" :key="method" class="slug-checkbox-label">
                    <input
                      type="checkbox"
                      :value="method"
                      :checked="form.rule_value_payment_methods.includes(method)"
                      @change="togglePaymentMethod(method)"
                    />
                    <span>{{ method }}</span>
                  </label>
                </div>
                <p class="form-hint">{{ $t('admin.compliance.jurisdiction.field_payment_methods_hint') }}</p>
              </div>
            </template>

            <!-- custom -->
            <template v-else-if="form.rule_type === 'custom'">
              <div class="form-group">
                <label class="form-label">{{ $t('admin.compliance.jurisdiction.field_custom_json') }} <span class="required">*</span></label>
                <textarea
                  v-model="form.rule_value_custom"
                  class="form-textarea"
                  rows="4"
                  placeholder='{"key": "value"}'
                ></textarea>
                <p v-if="customJsonError" class="field-error">{{ customJsonError }}</p>
              </div>
            </template>
          </div>

          <!-- Description -->
          <div class="form-group">
            <label class="form-label">{{ $t('admin.compliance.jurisdiction.field_description') }}</label>
            <textarea
              v-model="form.description"
              class="form-textarea"
              rows="2"
              :placeholder="$t('admin.compliance.jurisdiction.field_description_placeholder')"
            ></textarea>
          </div>

          <!-- Active / Priority -->
          <div class="form-row">
            <div class="form-group">
              <label class="form-label">
                <input type="checkbox" v-model="form.is_active" />
                &nbsp;{{ $t('admin.compliance.jurisdiction.field_active') }}
              </label>
            </div>
            <div class="form-group">
              <label class="form-label">{{ $t('admin.compliance.jurisdiction.field_priority') }}</label>
              <input
                v-model.number="form.priority"
                type="number"
                class="form-input"
                min="0"
                max="9999"
              />
            </div>
          </div>

          <p v-if="submitError" class="submit-error">{{ submitError }}</p>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" :disabled="form.processing" @click="closeModal">{{ $t('admin.compliance.jurisdiction.btn_cancel') }}</button>
          <button class="btn btn-primary" :disabled="form.processing || !form.rule_type" @click="submitForm">
            <span v-if="form.processing" class="spinner spinner-sm"></span>
            {{ form.processing ? $t('admin.compliance.jurisdiction.btn_saving') : (formModal.editId ? $t('admin.compliance.jurisdiction.btn_save_changes') : $t('admin.compliance.jurisdiction.btn_create')) }}
          </button>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div v-if="deleteModal.visible" class="modal-overlay" @click.self="deleteModal.visible = false">
      <div class="modal-content">
        <div class="modal-header">
          <h3 class="modal-title">{{ $t('admin.compliance.jurisdiction.modal_delete_title') }}</h3>
          <button class="modal-close" @click="deleteModal.visible = false">&times;</button>
        </div>
        <div class="modal-body">
          <p class="confirm-text">
            {{ $t('admin.compliance.jurisdiction.modal_delete_confirm', { type: formatRuleType(deleteModal.rule?.rule_type ?? ''), country: deleteModal.rule?.country_code }) }}
          </p>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" :disabled="deleteModal.processing" @click="deleteModal.visible = false">{{ $t('admin.compliance.jurisdiction.btn_cancel') }}</button>
          <button class="btn btn-danger" :disabled="deleteModal.processing" @click="executeDelete">
            <span v-if="deleteModal.processing" class="spinner spinner-sm"></span>
            {{ deleteModal.processing ? $t('admin.compliance.jurisdiction.btn_deleting') : $t('admin.compliance.jurisdiction.btn_delete') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import AppIcon from '@/components/AppIcon.vue'
import apiClient from '@/api/client'
import { useToastStore } from '@/stores/toast'

const { t } = useI18n()
const toast = useToastStore()

// ----------- Constants -----------

const RULE_TYPES = computed(() => [
  { value: 'blocked_country', label: t('admin.compliance.jurisdiction.rule_type_blocked_country') },
  { value: 'blocked_games', label: t('admin.compliance.jurisdiction.rule_type_blocked_games') },
  { value: 'deposit_limit', label: t('admin.compliance.jurisdiction.rule_type_deposit_limit') },
  { value: 'bonus_restriction', label: t('admin.compliance.jurisdiction.rule_type_bonus_restriction') },
  { value: 'required_kyc_tier', label: t('admin.compliance.jurisdiction.rule_type_required_kyc_tier') },
  { value: 'allowed_payment_methods', label: t('admin.compliance.jurisdiction.rule_type_allowed_payment_methods') },
  { value: 'custom', label: t('admin.compliance.jurisdiction.rule_type_custom') },
])

const PAYMENT_METHODS = ['manual', 'crypto', 'card'] as const

// ----------- Types -----------

interface RuleItem {
  id: number
  country_code: string
  region_code: string | null
  rule_type: string
  rule_value: Record<string, unknown>
  description: string | null
  is_active: boolean
  priority: number
  created_at: string
  updated_at: string
}

interface GameSlug {
  slug: string
  name: string
}

interface PaginatedResponse {
  data: RuleItem[]
  current_page: number
  last_page: number
  total: number
}

// ----------- State -----------

const rules = ref<RuleItem[]>([])
const loading = ref(true)
const currentPage = ref(1)
const totalPages = ref(0)
const totalRules = ref(0)

const gameSlugs = ref<GameSlug[]>([])
const gameSlugsLoading = ref(false)

const filters = ref({
  country: '',
  rule_type: '',
  active: '',
})

// Form modal
const formModal = reactive({
  visible: false,
  editId: null as number | null,
})

const form = reactive({
  country_code: '',
  region_code: '',
  rule_type: '',
  description: '',
  is_active: false,
  priority: 0,
  processing: false,
  // Per-type rule_value bindings
  rule_value_blocked_country: { message: '' },
  rule_value_blocked_games: [] as string[],
  rule_value_deposit_limit: { daily_limit: '' as string | number, monthly_limit: '' as string | number },
  rule_value_bonus_restriction: { allowed: true, max_bonus_percent: '' as string | number },
  rule_value_kyc_tier: { tier: '' },
  rule_value_payment_methods: [] as string[],
  rule_value_custom: '{}',
})

const formErrors = reactive<Record<string, string>>({})
const submitError = ref('')
const customJsonError = ref('')

// Delete modal
const deleteModal = reactive({
  visible: false,
  processing: false,
  rule: null as RuleItem | null,
})

// ----------- Computed -----------

const rangeStart = computed(() => {
  if (totalRules.value === 0) return 0
  return (currentPage.value - 1) * 50 + 1
})

const rangeEnd = computed(() => Math.min(currentPage.value * 50, totalRules.value))

const visiblePages = computed(() => {
  const pages: number[] = []
  const total = totalPages.value
  const current = currentPage.value
  let start = Math.max(1, current - 2)
  let end = Math.min(total, current + 2)
  if (end - start < 4) {
    if (start === 1) end = Math.min(total, start + 4)
    else start = Math.max(1, end - 4)
  }
  for (let i = start; i <= end; i++) pages.push(i)
  return pages
})

// ----------- Data Fetching -----------

async function fetchRules(): Promise<void> {
  loading.value = true
  try {
    const params: Record<string, string | number> = { page: currentPage.value }
    if (filters.value.country) params.country = filters.value.country.toUpperCase()
    if (filters.value.rule_type) params.rule_type = filters.value.rule_type
    if (filters.value.active) params.active = filters.value.active

    const response = await apiClient.get<PaginatedResponse>('/admin/api/jurisdiction-rules', { params })
    const data = response.data
    rules.value = data.data
    currentPage.value = data.current_page
    totalPages.value = data.last_page
    totalRules.value = data.total
  } catch {
    toast.error(t('admin.compliance.jurisdiction.error_load'))
  } finally {
    loading.value = false
  }
}

async function loadGameSlugs(): Promise<void> {
  if (gameSlugs.value.length > 0) return
  gameSlugsLoading.value = true
  try {
    const response = await apiClient.get<{ data: GameSlug[] }>('/admin/api/jurisdiction-rules/game-slugs')
    gameSlugs.value = response.data.data
  } catch {
    toast.error(t('admin.compliance.jurisdiction.error_load_games'))
  } finally {
    gameSlugsLoading.value = false
  }
}

function resetAndFetch() {
  currentPage.value = 1
  fetchRules()
}

// ----------- Modal -----------

function openCreateModal() {
  formModal.editId = null
  resetForm()
  formModal.visible = true
}

function openEditModal(rule: RuleItem) {
  formModal.editId = rule.id
  resetForm()

  form.country_code = rule.country_code
  form.region_code = rule.region_code ?? ''
  form.rule_type = rule.rule_type
  form.description = rule.description ?? ''
  form.is_active = rule.is_active
  form.priority = rule.priority

  // Populate per-type bindings from rule_value
  const rv = rule.rule_value

  if (rule.rule_type === 'blocked_country') {
    form.rule_value_blocked_country.message = (rv.message as string) ?? ''
  } else if (rule.rule_type === 'blocked_games') {
    form.rule_value_blocked_games = Array.isArray(rv.game_slugs) ? (rv.game_slugs as string[]) : []
    loadGameSlugs()
  } else if (rule.rule_type === 'deposit_limit') {
    // Stored in cents, display in dollars
    form.rule_value_deposit_limit.daily_limit = rv.daily_limit != null
      ? Math.round((rv.daily_limit as number) / 100)
      : ''
    form.rule_value_deposit_limit.monthly_limit = rv.monthly_limit != null
      ? Math.round((rv.monthly_limit as number) / 100)
      : ''
  } else if (rule.rule_type === 'bonus_restriction') {
    form.rule_value_bonus_restriction.allowed = (rv.allowed as boolean) ?? true
    form.rule_value_bonus_restriction.max_bonus_percent = rv.max_bonus_percent != null
      ? (rv.max_bonus_percent as number)
      : ''
  } else if (rule.rule_type === 'required_kyc_tier') {
    form.rule_value_kyc_tier.tier = (rv.tier as string) ?? ''
  } else if (rule.rule_type === 'allowed_payment_methods') {
    form.rule_value_payment_methods = Array.isArray(rv.methods) ? (rv.methods as string[]) : []
  } else if (rule.rule_type === 'custom') {
    form.rule_value_custom = JSON.stringify(rv, null, 2)
  }

  formModal.visible = true
}

function closeModal() {
  if (form.processing) return
  formModal.visible = false
}

function resetForm() {
  form.country_code = ''
  form.region_code = ''
  form.rule_type = ''
  form.description = ''
  form.is_active = false
  form.priority = 0
  form.processing = false
  form.rule_value_blocked_country.message = ''
  form.rule_value_blocked_games = []
  form.rule_value_deposit_limit.daily_limit = ''
  form.rule_value_deposit_limit.monthly_limit = ''
  form.rule_value_bonus_restriction.allowed = true
  form.rule_value_bonus_restriction.max_bonus_percent = ''
  form.rule_value_kyc_tier.tier = ''
  form.rule_value_payment_methods = []
  form.rule_value_custom = '{}'
  Object.keys(formErrors).forEach((k) => delete formErrors[k])
  submitError.value = ''
  customJsonError.value = ''
}

function resetRuleValue() {
  form.rule_value_blocked_country.message = ''
  form.rule_value_blocked_games = []
  form.rule_value_deposit_limit.daily_limit = ''
  form.rule_value_deposit_limit.monthly_limit = ''
  form.rule_value_bonus_restriction.allowed = true
  form.rule_value_bonus_restriction.max_bonus_percent = ''
  form.rule_value_kyc_tier.tier = ''
  form.rule_value_payment_methods = []
  form.rule_value_custom = '{}'
  customJsonError.value = ''

  if (form.rule_type === 'blocked_games') loadGameSlugs()
}

// ----------- Form Helpers -----------

function toggleGameSlug(slug: string) {
  const idx = form.rule_value_blocked_games.indexOf(slug)
  if (idx === -1) {
    form.rule_value_blocked_games.push(slug)
  } else {
    form.rule_value_blocked_games.splice(idx, 1)
  }
}

function togglePaymentMethod(method: string) {
  const idx = form.rule_value_payment_methods.indexOf(method)
  if (idx === -1) {
    form.rule_value_payment_methods.push(method)
  } else {
    form.rule_value_payment_methods.splice(idx, 1)
  }
}

function buildRuleValue(): Record<string, unknown> | null {
  const rt = form.rule_type

  if (rt === 'blocked_country') {
    return { blocked: true, message: form.rule_value_blocked_country.message || 'Gambling is not permitted in your country.' }
  }

  if (rt === 'blocked_games') {
    return { game_slugs: form.rule_value_blocked_games }
  }

  if (rt === 'deposit_limit') {
    const rv: Record<string, unknown> = {}
    if (form.rule_value_deposit_limit.daily_limit !== '') {
      rv.daily_limit = Number(form.rule_value_deposit_limit.daily_limit)
    }
    if (form.rule_value_deposit_limit.monthly_limit !== '') {
      rv.monthly_limit = Number(form.rule_value_deposit_limit.monthly_limit)
    }
    return rv
  }

  if (rt === 'bonus_restriction') {
    const rv: Record<string, unknown> = { allowed: form.rule_value_bonus_restriction.allowed }
    if (form.rule_value_bonus_restriction.allowed && form.rule_value_bonus_restriction.max_bonus_percent !== '') {
      rv.max_bonus_percent = Number(form.rule_value_bonus_restriction.max_bonus_percent)
    }
    return rv
  }

  if (rt === 'required_kyc_tier') {
    return { tier: form.rule_value_kyc_tier.tier }
  }

  if (rt === 'allowed_payment_methods') {
    return { methods: form.rule_value_payment_methods }
  }

  if (rt === 'custom') {
    try {
      customJsonError.value = ''
      return JSON.parse(form.rule_value_custom) as Record<string, unknown>
    } catch {
      customJsonError.value = t('admin.compliance.jurisdiction.error_invalid_json')
      return null
    }
  }

  return {}
}

// ----------- Submit -----------

async function submitForm(): Promise<void> {
  Object.keys(formErrors).forEach((k) => delete formErrors[k])
  submitError.value = ''

  const ruleValue = buildRuleValue()
  if (ruleValue === null) return  // JSON parse error already shown

  const payload = {
    country_code: form.country_code.toUpperCase(),
    region_code: form.region_code.trim() || null,
    rule_type: form.rule_type,
    rule_value: ruleValue,
    description: form.description.trim() || null,
    is_active: form.is_active,
    priority: form.priority,
  }

  form.processing = true
  try {
    if (formModal.editId) {
      await apiClient.put(`/admin/api/jurisdiction-rules/${formModal.editId}`, payload)
      toast.success(t('admin.compliance.jurisdiction.toast_updated'))
    } else {
      await apiClient.post('/admin/api/jurisdiction-rules', payload)
      toast.success(t('admin.compliance.jurisdiction.toast_created'))
    }
    closeModal()
    fetchRules()
  } catch (err: unknown) {
    const axiosErr = err as { response?: { data?: { errors?: Record<string, string[]>; message?: string } } }
    if (axiosErr.response?.data?.errors) {
      const errors = axiosErr.response.data.errors
      Object.entries(errors).forEach(([field, messages]) => {
        formErrors[field] = messages[0] ?? ''
      })
    } else {
      submitError.value = axiosErr.response?.data?.message ?? t('admin.compliance.jurisdiction.error_save')
    }
  } finally {
    form.processing = false
  }
}

// ----------- Delete -----------

function confirmDelete(rule: RuleItem) {
  deleteModal.rule = rule
  deleteModal.processing = false
  deleteModal.visible = true
}

async function executeDelete(): Promise<void> {
  if (!deleteModal.rule) return
  deleteModal.processing = true
  try {
    await apiClient.delete(`/admin/api/jurisdiction-rules/${deleteModal.rule.id}`)
    toast.success(t('admin.compliance.jurisdiction.toast_deleted'))
    deleteModal.visible = false
    fetchRules()
  } catch {
    toast.error(t('admin.compliance.jurisdiction.error_delete'))
  } finally {
    deleteModal.processing = false
  }
}

// ----------- Pagination -----------

function goToPage(page: number) {
  if (page < 1 || page > totalPages.value) return
  currentPage.value = page
  fetchRules()
}

// ----------- Formatting -----------

function formatRuleType(ruleType: string): string {
  const found = RULE_TYPES.value.find((rt) => rt.value === ruleType)
  return found?.label ?? ruleType
}

function typeTagClass(ruleType: string): string {
  switch (ruleType) {
    case 'blocked_country': return 'tag-danger'
    case 'blocked_games': return 'tag-warning'
    case 'deposit_limit': return 'tag-info'
    case 'bonus_restriction': return 'tag-accent'
    case 'required_kyc_tier': return 'tag-kyc'
    case 'allowed_payment_methods': return 'tag-payment'
    default: return 'tag-default'
  }
}

function summariseRuleValue(ruleType: string, ruleValue: Record<string, unknown>): string {
  if (ruleType === 'blocked_country') {
    return (ruleValue.message as string) ?? 'Country blocked'
  }
  if (ruleType === 'blocked_games') {
    const slugs = ruleValue.game_slugs as string[] | undefined
    if (!slugs || slugs.length === 0) return 'All games blocked'
    return `${slugs.length} game(s): ${slugs.slice(0, 3).join(', ')}${slugs.length > 3 ? '…' : ''}`
  }
  if (ruleType === 'deposit_limit') {
    const parts: string[] = []
    if (ruleValue.daily_limit != null) {
      parts.push(`Daily: $${((ruleValue.daily_limit as number) / 100).toFixed(0)}`)
    }
    if (ruleValue.monthly_limit != null) {
      parts.push(`Monthly: $${((ruleValue.monthly_limit as number) / 100).toFixed(0)}`)
    }
    return parts.join(' · ') || 'No limits set'
  }
  if (ruleType === 'bonus_restriction') {
    if (!(ruleValue.allowed as boolean)) return 'Bonuses disabled'
    const cap = ruleValue.max_bonus_percent
    return cap != null ? `Max ${cap}% bonus` : 'Bonuses allowed'
  }
  if (ruleType === 'required_kyc_tier') {
    return `Requires ${(ruleValue.tier as string) ?? 'basic'} KYC`
  }
  if (ruleType === 'allowed_payment_methods') {
    const methods = ruleValue.methods as string[] | undefined
    if (!methods || methods.length === 0) return 'No restrictions'
    return `Allowed: ${methods.join(', ')}`
  }
  if (ruleType === 'custom') {
    return JSON.stringify(ruleValue).slice(0, 60) + '…'
  }
  return JSON.stringify(ruleValue).slice(0, 60)
}

// ----------- Lifecycle -----------

onMounted(() => {
  fetchRules()
})
</script>

<style scoped>
.jurisdiction-page {
  padding: 24px;
  animation: fadeIn 0.3s ease both;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

/* Page Header */
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
}

.page-title {
  font-size: 28px;
  font-weight: 700;
  color: var(--text-primary, #fff);
  margin: 0 0 4px;
}

.page-subtitle {
  font-size: 13px;
  color: var(--text-tertiary, #7f8fa4);
  margin: 0;
}

/* Filter Bar */
.filter-bar {
  background: var(--bg-secondary, #1a2c38);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: var(--radius-lg, 12px);
  padding: 16px;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.filter-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.filter-label {
  font-size: 12px;
  color: var(--text-tertiary, #7f8fa4);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 600;
  white-space: nowrap;
}

.filter-input {
  background: var(--bg-tertiary, #213743);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: var(--radius-md, 8px);
  color: var(--text-primary, #fff);
  padding: 9px 12px;
  font-size: 13px;
  font-family: inherit;
  width: 100px;
  transition: border-color 150ms ease;
}

.filter-input:focus {
  outline: none;
  border-color: rgba(20, 117, 225, 0.5);
}

.filter-input::placeholder { color: var(--text-tertiary, #7f8fa4); }

.filter-bar select {
  background: var(--bg-tertiary, #213743);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: var(--radius-md, 8px);
  color: var(--text-primary, #fff);
  padding: 9px 12px;
  font-size: 13px;
  font-family: inherit;
  cursor: pointer;
  width: 180px;
  transition: border-color 150ms ease;
}

.filter-bar select:focus { outline: none; border-color: rgba(20, 117, 225, 0.5); }
.filter-bar select option { background: var(--bg-secondary, #1a2c38); color: var(--text-primary, #fff); }

/* Loading */
.loading-state {
  display: flex; align-items: center; justify-content: center;
  gap: 10px; padding: 64px 20px;
  color: var(--text-tertiary, #7f8fa4); font-size: 14px;
}

.loading-inline {
  color: var(--text-tertiary, #7f8fa4);
  font-size: 13px;
  padding: 8px 0;
}

.spinner {
  display: inline-block; width: 16px; height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.2); border-top-color: #fff;
  border-radius: 50%; animation: spin 0.6s linear infinite;
}

.spinner-sm { width: 14px; height: 14px; border-width: 2px; }

@keyframes spin { to { transform: rotate(360deg); } }

/* Table */
.table-container {
  background: var(--bg-secondary, #1a2c38);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: var(--radius-lg, 12px);
  overflow: hidden;
}

table { width: 100%; border-collapse: collapse; }
thead { background: var(--bg-tertiary, #213743); }

th {
  padding: 12px 16px; text-align: left;
  font-size: 11px; font-weight: 700; letter-spacing: 0.1em;
  text-transform: uppercase; color: var(--text-tertiary, #7f8fa4);
  border-bottom: 1px solid rgba(255, 255, 255, 0.05); white-space: nowrap;
}

td {
  padding: 14px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
  font-size: 13px; color: var(--text-secondary, #b1bad3);
}

.rule-row { background: var(--bg-secondary, #1a2c38); transition: background 150ms ease; }
.rule-row:hover { background: #253d4f; }
tbody tr:last-child td { border-bottom: none; }

.empty-row {
  text-align: center; color: var(--text-tertiary, #7f8fa4);
  padding: 48px 16px !important;
}

.text-secondary { color: var(--text-tertiary, #7f8fa4); }

.mono-value {
  font-family: var(--font-mono, 'JetBrains Mono', monospace);
  color: var(--text-primary, #fff);
}

/* Country tag */
.country-tag {
  display: inline-flex; align-items: center; justify-content: center;
  min-width: 36px; padding: 4px 10px;
  background: var(--bg-tertiary, #213743);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: var(--radius-sm, 4px);
  font-family: var(--font-mono, monospace);
  font-size: 12px; font-weight: 700;
  color: var(--text-primary, #fff); letter-spacing: 0.05em;
}

/* Rule type tags */
.type-tag {
  display: inline-flex; align-items: center;
  padding: 4px 10px; border-radius: 9999px;
  font-size: 11px; font-weight: 600; letter-spacing: 0.02em;
  text-transform: uppercase; white-space: nowrap;
}

.tag-danger { background: rgba(237, 66, 69, 0.2); color: #ed4245; }
.tag-warning { background: rgba(255, 167, 38, 0.15); color: #ffa726; }
.tag-info { background: rgba(20, 117, 225, 0.15); color: #1475e1; }
.tag-accent { background: rgba(0, 231, 1, 0.12); color: #00e701; }
.tag-kyc { background: rgba(155, 89, 182, 0.2); color: #9b59b6; }
.tag-payment { background: rgba(52, 152, 219, 0.15); color: #3498db; }
.tag-default { background: rgba(127, 143, 164, 0.15); color: #7f8fa4; }

/* Summary cell */
.summary-cell {
  max-width: 260px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}

/* Badges */
.badge {
  display: inline-flex; align-items: center; justify-content: center;
  padding: 4px 10px; border-radius: 9999px;
  font-size: 11px; font-weight: 600; letter-spacing: 0.02em; text-transform: uppercase;
}

.badge-active { background: rgba(0, 231, 1, 0.15); color: #00e701; }
.badge-inactive { background: rgba(127, 143, 164, 0.15); color: #7f8fa4; }

/* Action buttons */
.action-buttons { display: flex; align-items: center; gap: 6px; }

.btn-icon {
  width: 32px; height: 32px; border-radius: var(--radius-md, 8px);
  background: var(--bg-tertiary, #213743);
  border: 1px solid rgba(255, 255, 255, 0.1);
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; transition: all 150ms ease;
  font-size: 14px; color: var(--text-tertiary, #7f8fa4);
}

.btn-icon:hover {
  background: rgba(0, 231, 1, 0.15); color: #00e701;
  border-color: rgba(0, 231, 1, 0.3);
}

.btn-icon-danger:hover {
  background: rgba(237, 66, 69, 0.15); color: #ed4245;
  border-color: rgba(237, 66, 69, 0.3);
}

/* Pagination */
.pagination {
  display: flex; align-items: center; justify-content: center;
  gap: 8px; padding: 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
}

.pagination-btn {
  width: 32px; height: 32px; border-radius: var(--radius-md, 8px);
  background: var(--bg-tertiary, #213743);
  border: 1px solid rgba(255, 255, 255, 0.1);
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; transition: all 150ms ease;
  color: var(--text-secondary, #b1bad3);
  font-size: 13px; font-weight: 500; font-family: inherit;
}

.pagination-btn:hover:not(:disabled) { background: #2a4455; color: var(--text-primary, #fff); }
.pagination-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.pagination-btn.active { background: var(--accent-primary, #00e701); color: #000; border-color: var(--accent-primary, #00e701); }
.pagination-info { font-size: 12px; color: var(--text-tertiary, #7f8fa4); margin: 0 12px; }

/* Buttons */
.btn {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 9px 16px; border-radius: var(--radius-md, 8px);
  font-size: 13px; font-weight: 600; cursor: pointer;
  border: none; font-family: inherit; transition: all 150ms ease;
}

.btn-secondary {
  background: var(--bg-tertiary, #213743); color: var(--text-secondary, #b1bad3);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.btn-secondary:hover { background: #2a4455; color: var(--text-primary, #fff); }

.btn-primary { background: var(--accent-primary, #00e701); color: #000; }
.btn-primary:hover:not(:disabled) { background: #00cc01; }

.btn-danger { background: #ed4245; color: #fff; }
.btn-danger:hover:not(:disabled) { background: #c0392b; }

.btn:disabled { opacity: 0.5; cursor: not-allowed; }

/* Column widths */
.col-country { width: 8%; }
.col-region { width: 8%; }
.col-type { width: 16%; }
.col-summary { width: 32%; }
.col-active { width: 8%; }
.col-priority { width: 8%; }
.col-actions { width: 10%; }

/* Modal */
.modal-overlay {
  position: fixed; inset: 0; background: rgba(0, 0, 0, 0.7);
  display: flex; align-items: center; justify-content: center;
  z-index: 1000; animation: fadeIn 0.15s ease;
}

.modal-content {
  background: var(--bg-secondary, #1a2c38);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: var(--radius-lg, 12px);
  width: 100%; max-width: 500px;
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.5);
  max-height: 90vh; overflow-y: auto;
}

.modal-wide { max-width: 640px; }

.modal-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 20px 24px; border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.modal-title { font-size: 18px; font-weight: 700; color: var(--text-primary, #fff); margin: 0; }

.modal-close {
  background: none; border: none; color: var(--text-tertiary, #7f8fa4);
  font-size: 24px; cursor: pointer; line-height: 1; padding: 0 4px; font-family: inherit;
}

.modal-close:hover { color: var(--text-primary, #fff); }

.modal-body { padding: 20px 24px; }

.modal-footer {
  display: flex; align-items: center; justify-content: flex-end;
  gap: 10px; padding: 16px 24px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
}

/* Form styles */
.form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }

.form-group { margin-bottom: 16px; }

.form-label {
  display: block; font-size: 13px; font-weight: 600;
  color: var(--text-secondary, #b1bad3); margin-bottom: 6px;
}

.form-input {
  width: 100%; background: var(--bg-tertiary, #213743);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: var(--radius-md, 8px); color: var(--text-primary, #fff);
  padding: 9px 12px; font-size: 13px; font-family: inherit;
  transition: border-color 150ms ease; box-sizing: border-box;
}

.form-input:focus { outline: none; border-color: rgba(20, 117, 225, 0.5); }
.form-input::placeholder { color: var(--text-tertiary, #7f8fa4); }

.form-select {
  width: 100%; background: var(--bg-tertiary, #213743);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: var(--radius-md, 8px); color: var(--text-primary, #fff);
  padding: 9px 12px; font-size: 13px; font-family: inherit;
  cursor: pointer; transition: border-color 150ms ease;
}

.form-select:focus { outline: none; border-color: rgba(20, 117, 225, 0.5); }
.form-select option { background: var(--bg-secondary, #1a2c38); color: var(--text-primary, #fff); }

.form-textarea {
  width: 100%; background: var(--bg-tertiary, #213743);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: var(--radius-md, 8px); color: var(--text-primary, #fff);
  padding: 10px 12px; font-size: 13px; font-family: inherit;
  resize: vertical; transition: border-color 150ms ease; box-sizing: border-box;
}

.form-textarea:focus { outline: none; border-color: rgba(20, 117, 225, 0.5); }
.form-textarea::placeholder { color: var(--text-tertiary, #7f8fa4); }

.form-hint { font-size: 11px; color: var(--text-tertiary, #7f8fa4); margin: 4px 0 0; }

.required { color: var(--color-danger, #ed4245); }

/* Rule value form section */
.rule-value-form {
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: var(--radius-md, 8px);
  padding: 16px; margin-bottom: 16px;
}

.form-group-title {
  font-size: 11px; font-weight: 700; letter-spacing: 0.1em;
  text-transform: uppercase; color: var(--text-tertiary, #7f8fa4);
  margin-bottom: 14px; padding-bottom: 8px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

/* Slug checkboxes */
.slug-checkboxes {
  display: grid; grid-template-columns: 1fr 1fr;
  gap: 6px; max-height: 200px; overflow-y: auto;
  background: var(--bg-tertiary, #213743);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: var(--radius-md, 8px);
  padding: 12px;
}

.slug-checkbox-label {
  display: flex; align-items: center; gap: 6px;
  font-size: 13px; color: var(--text-secondary, #b1bad3);
  cursor: pointer;
}

.slug-checkbox-label input[type="checkbox"] { cursor: pointer; }

.slug-code {
  font-size: 11px; color: var(--text-tertiary, #7f8fa4);
  background: rgba(0,0,0,0.2); padding: 1px 4px; border-radius: 3px;
}

/* Errors */
.field-error { font-size: 12px; color: #ed4245; margin: 4px 0 0; }
.submit-error {
  margin-top: 12px; padding: 10px 14px;
  background: rgba(237, 66, 69, 0.1); border: 1px solid rgba(237, 66, 69, 0.3);
  border-radius: var(--radius-md, 8px); font-size: 13px; color: #ed4245;
}

/* Delete confirm */
.confirm-text { font-size: 14px; color: var(--text-secondary, #b1bad3); line-height: 1.6; }
.confirm-text strong { color: var(--text-primary, #fff); }

/* Responsive */
@media (max-width: 1024px) {
  .summary-cell { max-width: 180px; }
}

@media (max-width: 768px) {
  .jurisdiction-page { padding: 16px; }
  .page-header { flex-direction: column; align-items: flex-start; gap: 12px; }
  .form-row { grid-template-columns: 1fr; gap: 0; }
  .slug-checkboxes { grid-template-columns: 1fr; }
}
</style>
